/**
 * 图片文件安全校验（前端第一道防线）
 *
 * 攻击手法：把脚本 / HTML 改名成 xxx.png 上传。浏览器、七牛、Nginx 都按「后缀」
 * 决定 Content-Type，返回 image/png，结果整站多了一个可执行脚本的公开链接。
 * 只校验后缀名、甚至只校验 file.type 都挡不住 —— file.type 本身就是浏览器按
 * 后缀猜出来的，攻击者完全可控。
 *
 * 这里做四层校验：
 * 1) 扩展名 + MIME 白名单（顺手把 svg 摘掉，它是唯一可携带脚本的「图片」）
 * 2) 文件头魔数（magic bytes），挡住「只改后缀」
 * 3) 文件头文本标记扫描，挡住「魔数正确 + 尾部追加 HTML/SVG」的 polyglot 文件
 * 4) 交给浏览器真实解码，解码失败 / 尺寸异常即拒绝，挡住「只伪造了 PNG 文件头」
 *
 * 提醒：前端校验只负责提升攻击成本与拦截误传，不构成安全边界。
 * 服务端（七牛回调 / 后端上传接口）仍需用 libmagic、ImageIO 等再校验一次，
 * 存储侧还应固定返回 Content-Type: image/*，避免按文件名嗅探类型。
 */

// 各格式文件头签名
const MAGIC_SIGNATURES = [
  {
    ext: 'png',
    mime: 'image/png',
    match: bytes =>
      bytes.length >= 8 &&
      bytes[0] === 0x89 &&
      bytes[1] === 0x50 &&
      bytes[2] === 0x4e &&
      bytes[3] === 0x47 &&
      bytes[4] === 0x0d &&
      bytes[5] === 0x0a &&
      bytes[6] === 0x1a &&
      bytes[7] === 0x0a
  },
  {
    ext: 'jpg',
    mime: 'image/jpeg',
    match: bytes =>
      bytes.length >= 3 &&
      bytes[0] === 0xff &&
      bytes[1] === 0xd8 &&
      bytes[2] === 0xff
  },
  {
    ext: 'gif',
    mime: 'image/gif',
    // GIF87a / GIF89a
    match: bytes =>
      bytes.length >= 4 &&
      bytes[0] === 0x47 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x38
  },
  {
    ext: 'bmp',
    mime: 'image/bmp',
    match: bytes => bytes.length >= 2 && bytes[0] === 0x42 && bytes[1] === 0x4d
  },
  {
    ext: 'webp',
    mime: 'image/webp',
    // RIFF....WEBP
    match: bytes =>
      bytes.length >= 12 &&
      bytes[0] === 0x52 &&
      bytes[1] === 0x49 &&
      bytes[2] === 0x46 &&
      bytes[3] === 0x46 &&
      bytes[8] === 0x57 &&
      bytes[9] === 0x45 &&
      bytes[10] === 0x42 &&
      bytes[11] === 0x50
  }
]

// 头部出现这些标记，说明文件内容其实是脚本 / 标记语言，不是位图
const DANGEROUS_MARKERS = [
  '<script',
  '<svg',
  '<html',
  '<iframe',
  '<!doctype',
  '<?php',
  'javascript:'
]

export const DEFAULT_IMAGE_EXTS = ['png', 'jpg', 'jpeg', 'gif', 'bmp', 'webp']

export const DEFAULT_IMAGE_MIMES = [
  'image/png',
  'image/jpeg',
  'image/jpg',
  'image/gif',
  'image/bmp',
  'image/x-ms-bmp',
  'image/webp'
]

const DEFAULT_MAX_SIZE = 4 * 1024 * 1024
const DEFAULT_MAX_PIXELS = 50 * 1000 * 1000
const DECODE_TIMEOUT = 8000

// File 对象 → 校验结果，避免一次选择被 antd 重复校验多次
const validatedCache = typeof WeakMap !== 'undefined' ? new WeakMap() : null

function createError(code, message) {
  const err = new Error(message)
  err.code = code
  return err
}

/**
 * 取文件扩展名（小写，不含点）
 */
export function getFileExt(name) {
  const value = String(name || '')
  const index = value.lastIndexOf('.')
  if (index < 0) return ''
  return value.slice(index + 1).toLowerCase()
}

/**
 * 读取文件开头若干字节
 */
export function readFileBytes(file, length = 512) {
  return new Promise((resolve, reject) => {
    const blob = typeof file.slice === 'function' ? file.slice(0, length) : file
    if (typeof blob.arrayBuffer === 'function') {
      blob
        .arrayBuffer()
        .then(buf => resolve(new Uint8Array(buf)))
        .catch(() => reject(createError('READ_FAILED', '文件读取失败')))
      return
    }
    const reader = new FileReader()
    reader.onload = () => resolve(new Uint8Array(reader.result))
    reader.onerror = () => reject(createError('READ_FAILED', '文件读取失败'))
    reader.readAsArrayBuffer(blob)
  })
}

/**
 * 根据文件头判断真实格式，识别不出来返回 null
 */
export function detectImageType(bytes) {
  if (!bytes || !bytes.length) return null
  for (let i = 0; i < MAGIC_SIGNATURES.length; i++) {
    const signature = MAGIC_SIGNATURES[i]
    if (signature.match(bytes)) {
      return { ext: signature.ext, mime: signature.mime }
    }
  }
  return null
}

/**
 * 扫描头部文本标记（按 latin1 解码，二进制文件不会被误伤为合法字符）
 */
export function hasDangerousMarkup(bytes) {
  if (!bytes || !bytes.length) return null
  let text = ''
  for (let i = 0; i < bytes.length; i++) {
    text += String.fromCharCode(bytes[i])
  }
  const lower = text.toLowerCase()
  for (let i = 0; i < DANGEROUS_MARKERS.length; i++) {
    if (lower.indexOf(DANGEROUS_MARKERS[i]) !== -1) {
      return DANGEROUS_MARKERS[i]
    }
  }
  return null
}

/**
 * 用浏览器真实解码一次，解码不出来就说明这不是一张能被渲染的图
 * 返回 { width, height }
 */
export function decodeImage(file, options = {}) {
  const timeout = options.timeout || DECODE_TIMEOUT
  return new Promise((resolve, reject) => {
    let settled = false
    let timer = null
    let objectUrl = ''

    const finish = (isOk, payload) => {
      if (settled) return
      settled = true
      if (timer) clearTimeout(timer)
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl)
        objectUrl = ''
      }
      if (isOk) resolve(payload)
      else reject(payload)
    }

    const settleBySize = (width, height) => {
      if (!width || !height) {
        finish(false, createError('DECODE_FAILED', '图片解码失败'))
        return
      }
      finish(true, { width, height })
    }

    // 兜底：老浏览器 / createImageBitmap 不支持该格式时走 Image
    const decodeByImage = () => {
      if (settled) return
      if (typeof Image === 'undefined' || typeof URL === 'undefined') {
        finish(
          false,
          createError('DECODE_UNSUPPORTED', '当前环境不支持图片解码校验')
        )
        return
      }
      const img = new Image()
      objectUrl = URL.createObjectURL(file)
      img.onload = () => settleBySize(img.naturalWidth, img.naturalHeight)
      img.onerror = () =>
        finish(false, createError('DECODE_FAILED', '图片解码失败'))
      img.src = objectUrl
    }

    timer = setTimeout(() => {
      finish(false, createError('DECODE_TIMEOUT', '图片解析超时'))
    }, timeout)

    if (typeof createImageBitmap === 'function') {
      createImageBitmap(file)
        .then(bitmap => {
          if (settled) {
            if (bitmap.close) bitmap.close()
            return
          }
          const width = bitmap.width
          const height = bitmap.height
          if (bitmap.close) bitmap.close()
          settleBySize(width, height)
        })
        .catch(() => decodeByImage())
      return
    }

    decodeByImage()
  })
}

/**
 * 组合校验入口
 * @param {File} file 用户选择的文件
 * @param {Object} options
 * @param {Array}  options.allowedExts 允许的扩展名
 * @param {Number} options.maxSize 体积上限（字节）
 * @param {Number} options.maxPixels 像素总数上限，防止解压炸弹
 * @param {Boolean} options.skipMarkupScan 是否跳过文本标记扫描
 * @returns {Promise<{ ok: boolean, code?: string, message?: string, width?: number, height?: number, mime?: string }>}
 */
export function validateImageFile(file, options = {}) {
  const {
    allowedExts = DEFAULT_IMAGE_EXTS,
    maxSize = DEFAULT_MAX_SIZE,
    maxPixels = DEFAULT_MAX_PIXELS,
    skipMarkupScan = false
  } = options

  if (!file) {
    return Promise.reject(createError('EMPTY_FILE', '未选择文件'))
  }

  if (validatedCache && validatedCache.has(file)) {
    return validatedCache.get(file)
  }

  const task = (async () => {
    const name = file.name || ''
    const ext = getFileExt(name)

    // 1) 扩展名白名单（svg 单独提示，避免用户以为 png 也能塞 svg）
    if (ext === 'svg') {
      throw createError('SVG_FORBIDDEN', '不支持 SVG 格式，请上传 png/jpg 图片')
    }
    if (!allowedExts.includes(ext)) {
      throw createError(
        'EXT_NOT_ALLOWED',
        `不支持的格式 .${ext || '未知'}，仅支持 ${allowedExts
          .map(e => '.' + e)
          .join('/')}`
      )
    }

    // 2) MIME 白名单（浏览器给空值时放过，以文件头为准）
    if (file.type && !DEFAULT_IMAGE_MIMES.includes(file.type)) {
      throw createError(
        'MIME_NOT_ALLOWED',
        '文件类型不合法，请上传真实的图片文件'
      )
    }

    if (file.size > maxSize) {
      throw createError(
        'SIZE_EXCEEDED',
        `资源文件体积不能超过 ${Math.round(maxSize / 1024 / 1024)}M`
      )
    }

    // 3) 文件头魔数
    const bytes = await readFileBytes(file, 512)
    const type = detectImageType(bytes)
    if (!type) {
      throw createError(
        'MAGIC_INVALID',
        '文件内容不是有效图片（可能被伪造了扩展名）'
      )
    }

    // 4) 头部文本标记，挡住 svg / html 伪装的 polyglot
    if (!skipMarkupScan) {
      const marker = hasDangerousMarkup(bytes)
      if (marker) {
        throw createError('MARKUP_DETECTED', '文件内容包含可执行标记，已拒绝')
      }
    }

    // 5) 真实解码，这一步才真正证明「浏览器认它是一张图」
    const size = await decodeImage(file, { timeout: DECODE_TIMEOUT })
    if (size.width * size.height > maxPixels) {
      throw createError('PIXELS_EXCEEDED', '图片尺寸过大，请压缩后再上传')
    }

    return {
      ok: true,
      width: size.width,
      height: size.height,
      mime: type.mime
    }
  })().catch(err => ({
    ok: false,
    code: err && err.code ? err.code : 'UNKNOWN',
    message: err && err.message ? err.message : '图片校验失败'
  }))

  if (validatedCache) {
    validatedCache.set(file, task)
  }
  return task
}
